import React, { useState, useEffect, useContext, useCallback } from "react"; // 1. Added useCallback
import { useNavigate, useParams } from "react-router-dom";
import { Spinner, Button, Form, Modal } from "react-bootstrap";
import {
  User,
  Trash2,
  RotateCw,
  Search,
  Printer,
  Download,
  Phone,
  Mail,
  FileText,
  AlertTriangle,
  Users,
} from "lucide-react";
import "bootstrap/dist/css/bootstrap.min.css";
import ChatbotButton from "../../components/common/ChatbotButton";
import { AuthContext } from "../../context/AuthContext";
import { userRoles } from "../../App";
import {
  participantsAPI,
  removeParticipantAPI,
} from "../../services/userService";

// PDF Libraries
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import "../../assets/styles/ParticipantsAdvanced.css";
import PopupMessage from "../../components/common/PopupMessage";

export default function Participants() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useContext(AuthContext);

  const [participants, setParticipants] = useState([]);
  const [activityInfo, setActivityInfo] = useState({
    name: "",
    supervisor: "",
  });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  // Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [participantToDelete, setParticipantToDelete] = useState(null);
  const [popup, setPopup] = useState({ show: false, type: "", message: "" });

  // 2. Wrap fetchData in useCallback to fix ESLint Warning
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await participantsAPI(id);
      setParticipants(response.participants || []);
      setActivityInfo(
        response.activityInfo || { name: "Activity", supervisor: "N/A" },
      );
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  }, [id]); // Dependency ensures function only recreates if ID changes

  // 3. Now safely include fetchData in dependency array
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter Logic
  const filteredParticipants = participants.filter((p) => {
    const query = search.toLowerCase();
    return (
      p.fullName.toLowerCase().includes(query) ||
      p.universtyID.toLowerCase().includes(query) ||
      p.email.toLowerCase().includes(query)
    );
  });

  // --- DELETE LOGIC ---
  const handleRemoveClick = (p) => {
    setParticipantToDelete(p);
    setShowDeleteModal(true);
  };

  const confirmRemove = async () => {
    if (!participantToDelete) return;

    try {
      await removeParticipantAPI(id, participantToDelete._id);

      // تحديث الليست محليًا
      setParticipants((prev) =>
        prev.filter((p) => p._id !== participantToDelete._id),
      );

      // قفل المودال وتنضيف الستيت
      setShowDeleteModal(false);
      setParticipantToDelete(null);
      setPopup({
        show: true,
        type: "success",
        message: "Participant removed successfully",
      });
    } catch (error) {
      console.error(error);
      const message =
        error?.response?.data?.message || "Failed to remove participant";
      setPopup({
        show: true,
        type: "error",
        message: message || "Failed to remove participant",
      });
    }
  };

  // --- 1. Browser Print ---
  const handleBrowserPrint = () => {
    window.print();
  };

  // --- 2. PDF Download ---
  const generatePDF = () => {
    const doc = new jsPDF();
    const actName = activityInfo.name || "Activity";
    const supName = activityInfo.supervisor || "Unknown";

    // Header Background
    doc.setFillColor(248, 249, 250);
    doc.rect(0, 0, 210, 45, "F");

    // Title
    doc.setFontSize(22);
    doc.setTextColor(33, 37, 41);
    doc.text("Participants Report", 14, 20);

    // Sub-info
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Activity: ${actName}`, 14, 30);
    doc.text(`Supervisor: ${supName}`, 14, 37);

    // Date & Count
    const dateStr = new Date().toLocaleDateString();
    doc.text(`Date: ${dateStr}`, 150, 30);
    doc.text(`Count: ${filteredParticipants.length}`, 150, 37);

    // Table
    const tableColumn = ["#", "Name", "ID", "Email", "Phone"];
    const tableRows = filteredParticipants.map((p, index) => [
      index + 1,
      p.fullName,
      p.universtyID,
      p.email,
      p.phone || "N/A",
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 50,
      theme: "grid",
      headStyles: { fillColor: [55, 65, 81], textColor: 255, fontSize: 10 },
      styles: { fontSize: 10, cellPadding: 3 },
      alternateRowStyles: { fillColor: [250, 250, 250] },
    });

    doc.save(`${actName}_Participants.pdf`);
  };

  const getInitials = (name) =>
    name
      ? name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .substring(0, 2)
          .toUpperCase()
      : "ST";

  return (
    <div className="participants-page container-fluid">
      {/* HEADER */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4 mt-5 pt-4 no-print gap-3">
        <div className="mt-3">
          <h2 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
            <Users size={26} className="text-primary" />
            Participants
            <span className="text-muted fw-normal fs-5">
              ({filteredParticipants.length})
            </span>
          </h2>
          <p className="text-muted mb-0 small">
            Activity:{" "}
            <span className="fw-semibold text-dark">
              {activityInfo.name || "Loading..."}
            </span>
          </p>
        </div>

        <div className="d-flex gap-2 w-100 w-md-auto justify-content-md-end flex-wrap">
          <Button
            variant="white"
            className="shadow-sm border fw-bold text-muted"
            onClick={fetchData}
            title="Refresh"
          >
            <RotateCw size={18} className={loading ? "spin-anim" : ""} />
          </Button>

          <Button
            variant="white"
            className="shadow-sm border fw-bold text-dark d-flex align-items-center gap-2"
            onClick={handleBrowserPrint}
          >
            <Printer size={18} /> Print
          </Button>

          <Button
            variant="dark"
            className="shadow-sm fw-bold d-flex align-items-center gap-2"
            onClick={generatePDF}
          >
            <Download size={18} />{" "}
            <span className="d-none d-sm-inline">Export PDF</span>
          </Button>
        </div>
      </div>

      {/* SEARCH */}
      <div className="card border-0 shadow-sm rounded-pill px-3 py-1 mb-4 no-print search-card">
        <div className="input-group">
          <span className="input-group-text bg-white border-0">
            <Search size={18} className="text-muted" />
          </span>
          <Form.Control
            type="text"
            placeholder="Search by name, ID, or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border-0 shadow-none"
            style={{ fontSize: "0.95rem" }}
          />
        </div>
      </div>

      {/* PRINTABLE HEADER */}
      <div className="print-only-header text-center mb-4">
        <h2>{activityInfo.name} - Participants List</h2>
        <p className="mb-1">Supervisor: {activityInfo.supervisor}</p>
        <p>
          Total Students: {filteredParticipants.length} | Date:{" "}
          {new Date().toLocaleDateString()}
        </p>
        <hr />
      </div>

      {/* CONTENT */}
      <div className="content-wrapper">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted small">Fetching data...</p>
          </div>
        ) : filteredParticipants.length > 0 ? (
          <>
            {/* Desktop Table */}
            <div className="table-responsive d-none d-md-block shadow-sm rounded-4 bg-white border">
              <table className="table table-hover align-middle mb-0 custom-table">
                <thead className="bg-light">
                  <tr>
                    <th className="ps-4 py-3">Student Name</th>
                    <th>ID</th>
                    <th>Contact Info</th>
                    <th className="text-end pe-4 no-print">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredParticipants.map((p) => (
                    <tr key={p._id}>
                      <td className="ps-4">
                        <div className="d-flex align-items-center gap-3">
                          {/* Avatar Circle - Will be hidden in print via CSS */}
                          <div className="avatar-circle bg-light text-primary fw-bold border">
                            {getInitials(p.fullName)}
                          </div>
                          <div>
                            <div className="fw-bold text-dark">
                              {p.fullName}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="bg-white text-dark rounded-4 border fw-normal px-2 py-1">
                          {p.universtyID}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex flex-column small text-muted">
                          <span>{p.email}</span>
                          <span>{p.phone || "N/A"}</span>
                        </div>
                      </td>
                      <td className="text-end pe-4 no-print">
                        <div className="d-flex justify-content-end gap-2">
                          <Button
                            size="sm"
                            variant="light"
                            className="btn-icon border"
                            onClick={() =>
                              navigate(`/admin/view-user-profile/${p._id}`)
                            }
                          >
                            <User size={15} />
                          </Button>
                          {user?.role === userRoles.admin && (
                            <Button
                              size="sm"
                              variant="light"
                              className="btn-icon border text-danger hover-danger"
                              onClick={() => handleRemoveClick(p)}
                            >
                              <Trash2 size={15} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="d-md-none d-flex flex-column gap-3">
              {filteredParticipants.map((p) => (
                <div
                  key={p._id}
                  className="bg-white p-3 rounded-4 shadow-sm border participant-card"
                >
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="avatar-circle-sm bg-primary text-white fw-bold">
                        {getInitials(p.fullName)}
                      </div>
                      <div>
                        <h6 className="fw-bold mb-0 text-dark">{p.fullName}</h6>
                        <small className="text-muted">{p.universtyID}</small>
                      </div>
                    </div>
                    {user?.role === userRoles.admin && (
                      <button
                        className="btn btn-sm text-danger p-0 no-print"
                        onClick={() => handleRemoveClick(p)}
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                  <div className="d-flex flex-column gap-1 mb-3 px-1 small text-muted">
                    <div className="d-flex align-items-center gap-2">
                      <Mail size={14} /> {p.email}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Phone size={14} /> {p.phone || "N/A"}
                    </div>
                  </div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="w-100 rounded-pill no-print"
                    onClick={() =>
                      navigate(`/admin/view-user-profile/${p._id}`)
                    }
                  >
                    View Profile
                  </Button>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="empty-state text-center py-5">
            <div className="bg-light rounded-circle d-inline-flex p-4 mb-3">
              <FileText size={32} className="text-muted" />
            </div>
            <h6 className="text-dark">No Participants Found</h6>
            <p className="text-muted small">No students matched your search.</p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Body className="text-center p-4">
          <div
            className="mx-auto bg-danger bg-opacity-10 text-danger rounded-circle d-flex align-items-center justify-content-center mb-3"
            style={{ width: "55px", height: "55px" }}
          >
            <AlertTriangle size={28} />
          </div>
          <h5 className="fw-bold text-dark">Remove Participant?</h5>
          <p className="text-muted mb-4 small">
            Are you sure you want to remove{" "}
            <strong className="text-dark">
              {participantToDelete?.fullName}
            </strong>
            ?
          </p>
          <div className="d-flex gap-2 justify-content-center">
            <Button
              variant="light"
              className="px-4 rounded-pill fw-bold"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              className="px-4 rounded-pill fw-bold"
              onClick={confirmRemove}
            >
              Confirm Remove
            </Button>
          </div>
        </Modal.Body>
      </Modal>
      {popup.show && (
        <PopupMessage
          type={popup.type}
          message={popup.message}
          onClose={() => setPopup({ ...popup, show: false })}
        />
      )}

      <ChatbotButton />
    </div>
  );
}

// import React, { useState, useEffect, useContext } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import { Spinner, Button, Form, Modal } from "react-bootstrap";
// import {
//   User, Trash2, RotateCw, Search, Printer,
//   Download, Phone, Mail, FileText, AlertTriangle, Users
// } from "lucide-react";
// import "bootstrap/dist/css/bootstrap.min.css";
// import ChatbotButton from "../../components/common/ChatbotButton";
// import { AuthContext } from "../../context/AuthContext";
// import { userRoles } from "../../App";
// import { participantsAPI } from "../../services/userService";

// // PDF Libraries
// import jsPDF from "jspdf";
// import autoTable from "jspdf-autotable";

// // CSS file import
// import "../../assets/styles/ParticipantsAdvanced.css";

// export default function Participants() {
//   const navigate = useNavigate();
//   const { id } = useParams();
//   const { user } = useContext(AuthContext);

//   const [participants, setParticipants] = useState([]);
//   const [activityInfo, setActivityInfo] = useState({ name: "", supervisor: "" });
//   const [loading, setLoading] = useState(false);
//   const [search, setSearch] = useState("");

//   // Modal State
//   const [showDeleteModal, setShowDeleteModal] = useState(false);
//   const [participantToDelete, setParticipantToDelete] = useState(null);

//   // 1. Fetch Data
//   const fetchData = async () => {
//     setLoading(true);
//     try {
//       const response = await participantsAPI(id);
//       setParticipants(response.participants || []);
//       // لو الباك إند مرجعش activityInfo بنحط قيم افتراضية
//       setActivityInfo(response.activityInfo || { name: "Activity", supervisor: "N/A" });
//     } catch (error) {
//       console.error("Error:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchData();
//   }, [id]);

//   // Filter Logic
//   const filteredParticipants = participants.filter((p) => {
//     const query = search.toLowerCase();
//     return (
//       p.fullName.toLowerCase().includes(query) ||
//       p.universtyID.toLowerCase().includes(query) ||
//       p.email.toLowerCase().includes(query)
//     );
//   });

//   // --- DELETE LOGIC ---
//   const handleRemoveClick = (p) => {
//     setParticipantToDelete(p);
//     setShowDeleteModal(true);
//   };

//   const confirmRemove = async () => {
//     if (!participantToDelete) return;
//     try {
//       // await removeParticipantAPI(id, participantToDelete._id);
//       setParticipants((prev) => prev.filter((x) => x._id !== participantToDelete._id));
//       setShowDeleteModal(false);
//       setParticipantToDelete(null);
//     } catch (error) {
//       alert("Failed to remove participant");
//     }
//   };

//   // --- 1. Browser Print ---
//   const handleBrowserPrint = () => {
//     window.print();
//   };

//   // --- 2. PDF Download ---
//   const generatePDF = () => {
//     const doc = new jsPDF();
//     const actName = activityInfo.name || "Activity";
//     const supName = activityInfo.supervisor || "Unknown";

//     // Header Background
//     doc.setFillColor(248, 249, 250);
//     doc.rect(0, 0, 210, 45, "F");

//     // Title
//     doc.setFontSize(22);
//     doc.setTextColor(33, 37, 41);
//     doc.text("Participants Report", 14, 20);

//     // Sub-info
//     doc.setFontSize(11);
//     doc.setTextColor(100);
//     doc.text(`Activity: ${actName}`, 14, 30);
//     doc.text(`Supervisor: ${supName}`, 14, 37);

//     // Date & Count (Right aligned)
//     const dateStr = new Date().toLocaleDateString();
//     doc.text(`Date: ${dateStr}`, 150, 30);
//     doc.text(`Count: ${filteredParticipants.length}`, 150, 37);

//     // Table
//     const tableColumn = ["#", "Name", "ID", "Email", "Phone"];
//     const tableRows = filteredParticipants.map((p, index) => [
//       index + 1,
//       p.fullName,
//       p.universtyID,
//       p.email,
//       p.phone || "N/A",
//     ]);

//     autoTable(doc, {
//       head: [tableColumn],
//       body: tableRows,
//       startY: 50,
//       theme: 'grid',
//       headStyles: { fillColor: [55, 65, 81], textColor: 255, fontSize: 10 },
//       styles: { fontSize: 10, cellPadding: 3 },
//       alternateRowStyles: { fillColor: [250, 250, 250] }
//     });

//     doc.save(`${actName}_Participants.pdf`);
//   };

//   // Helper: Initials Avatar
//   const getInitials = (name) => name ? name.split(" ").map((n) => n[0]).join("").substring(0, 2).toUpperCase() : "ST";

//   return (
//     <div style={{marginTop:"70px"}} className="participants-page container-fluid">

//       {/* === HEADER (Hidden on Print) === */}
//       <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4 mt-5 pt-4 no-print gap-3">
//         <div>
//           <h2 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
//             <Users size={26} className="text-primary" />
//             Participants
//             {/* Simple Counter instead of Badge */}
//             <span className="text-muted fw-normal fs-5">({filteredParticipants.length})</span>
//           </h2>
//           <p className="text-muted mb-0 small">
//             Activity: <span className="fw-semibold text-dark">{activityInfo.name || "Loading..."}</span>
//           </p>
//         </div>

//         {/* Action Buttons */}
//         <div className="d-flex gap-2 w-100 w-md-auto justify-content-md-end flex-wrap">
//           <Button variant="white" className="shadow-sm border fw-bold text-muted" onClick={fetchData} title="Refresh">
//             <RotateCw size={18} className={loading ? "spin-anim" : ""} />
//           </Button>

//           {/* Print Button Restored */}
//           <Button variant="white" className="shadow-sm border fw-bold text-dark d-flex align-items-center gap-2" onClick={handleBrowserPrint}>
//             <Printer size={18} /> Print
//           </Button>

//           {/* PDF Button */}
//           <Button variant="dark" className="shadow-sm fw-bold d-flex align-items-center gap-2" onClick={generatePDF}>
//             <Download size={18} /> <span className="d-none d-sm-inline">Export PDF</span>
//           </Button>
//         </div>
//       </div>

//       {/* === SEARCH BAR (Hidden on Print) === */}
//       <div className="card border-0 shadow-sm rounded-pill px-3 py-1 mb-4 no-print search-card">
//         <div className="input-group">
//           <span className="input-group-text bg-white border-0"><Search size={18} className="text-muted" /></span>
//           <Form.Control
//             type="text"
//             placeholder="Search by name, ID, or email..."
//             value={search}
//             onChange={(e) => setSearch(e.target.value)}
//             className="border-0 shadow-none"
//             style={{ fontSize: "0.95rem" }}
//           />
//         </div>
//       </div>

//       {/* === PRINTABLE HEADER (Visible ONLY on Print) === */}
//       <div className="print-only-header text-center mb-4">
//         <h2>{activityInfo.name} - Participants List</h2>
//         <p className="mb-1">Supervisor: {activityInfo.supervisor}</p>
//         <p>Total Students: {filteredParticipants.length} | Date: {new Date().toLocaleDateString()}</p>
//         <hr />
//       </div>

//       {/* === MAIN CONTENT === */}
//       <div className="content-wrapper">
//         {loading ? (
//           <div className="text-center py-5">
//             <Spinner animation="border" variant="primary" />
//             <p className="mt-2 text-muted small">Fetching data...</p>
//           </div>
//         ) : filteredParticipants.length > 0 ? (
//           <>
//             {/* Desktop Table */}
//             <div className="table-responsive d-none d-md-block shadow-sm rounded-4 bg-white border">
//               <table className="table table-hover align-middle mb-0 custom-table">
//                 <thead className="bg-light">
//                   <tr>
//                     <th className="ps-4 py-3">Student Name</th>
//                     <th>ID</th>
//                     <th>Contact Info</th>
//                     <th className="text-end pe-4 no-print">Actions</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {filteredParticipants.map((p) => (
//                     <tr key={p._id}>
//                       <td className="ps-4">
//                         <div className="d-flex align-items-center gap-3">
//                           <div className="avatar-circle bg-light text-primary fw-bold border">{getInitials(p.fullName)}</div>
//                           <div>
//                             <div className="fw-bold text-dark">{p.fullName}</div>
//                           </div>
//                         </div>
//                       </td>
//                       <td><span className="bg-white text-dark rounded-4 border fw-normal px-2 py-1">{p.universtyID}</span></td>
//                       <td>
//                         <div className="d-flex flex-column small text-muted">
//                           <span>{p.email}</span>
//                           <span>{p.phone || "N/A"}</span>
//                         </div>
//                       </td>
//                       <td className="text-end pe-4 no-print">
//                         <div className="d-flex justify-content-end gap-2">
//                           <Button size="sm" variant="light" className="btn-icon border" onClick={() => navigate(`/admin/view-user-profile/${p._id}`)}>
//                             <User size={15} />
//                           </Button>
//                           {user?.role === userRoles.admin && (
//                             <Button size="sm" variant="light" className="btn-icon border text-danger hover-danger" onClick={() => handleRemoveClick(p)}>
//                               <Trash2 size={15} />
//                             </Button>
//                           )}
//                         </div>
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>

//             {/* Mobile Cards */}
//             <div className="d-md-none d-flex flex-column gap-3">
//               {filteredParticipants.map((p) => (
//                 <div key={p._id} className="bg-white p-3 rounded-4 shadow-sm border participant-card">
//                   <div className="d-flex justify-content-between align-items-start mb-3">
//                     <div className="d-flex align-items-center gap-3">
//                       <div className="avatar-circle-sm bg-primary text-white fw-bold">{getInitials(p.fullName)}</div>
//                       <div>
//                         <h6 className="fw-bold mb-0 text-dark">{p.fullName}</h6>
//                         <small className="text-muted">{p.universtyID}</small>
//                       </div>
//                     </div>
//                     {user?.role === userRoles.admin && (
//                       <button className="btn btn-sm text-danger p-0 no-print" onClick={() => handleRemoveClick(p)}>
//                         <Trash2 size={18} />
//                       </button>
//                     )}
//                   </div>
//                   <div className="d-flex flex-column gap-1 mb-3 px-1 small text-muted">
//                     <div className="d-flex align-items-center gap-2"><Mail size={14} /> {p.email}</div>
//                     <div className="d-flex align-items-center gap-2"><Phone size={14} /> {p.phone || "N/A"}</div>
//                   </div>
//                   <Button variant="outline-primary" size="sm" className="w-100 rounded-pill no-print" onClick={() => navigate(`/admin/view-user-profile/${p._id}`)}>
//                     View Profile
//                   </Button>
//                 </div>
//               ))}
//             </div>
//           </>
//         ) : (
//           <div className="empty-state text-center py-5">
//             <div className="bg-light rounded-circle d-inline-flex p-4 mb-3"><FileText size={32} className="text-muted" /></div>
//             <h6 className="text-dark">No Participants Found</h6>
//             <p className="text-muted small">No students matched your search.</p>
//           </div>
//         )}
//       </div>

//       {/* Delete Confirmation Modal */}
//       <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered backdrop="static">
//         <Modal.Body className="text-center p-4">
//           <div className="mx-auto bg-danger bg-opacity-10 text-danger rounded-circle d-flex align-items-center justify-content-center mb-3" style={{width: '55px', height: '55px'}}>
//             <AlertTriangle size={28} />
//           </div>
//           <h5 className="fw-bold text-danger">Remove Participant?</h5>
//           <p className="text-muted mb-4 small">
//             Are you sure you want to remove <strong className="text-dark">{participantToDelete?.fullName}</strong>?
//           </p>
//           <div className="d-flex gap-2 justify-content-center">
//             <Button variant="light" className="px-4 rounded-pill fw-bold" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
//             <Button variant="danger" className="px-4 rounded-pill fw-bold" onClick={confirmRemove}>Confirm Remove</Button>
//           </div>
//         </Modal.Body>
//       </Modal>

//       <ChatbotButton />
//     </div>
//   );
// }
